import { useCallback, useEffect, useRef } from 'react';
import type { AppStateStatus, NativeEventSubscription } from 'react-native';
import { AppState } from 'react-native';

import { useTrackScreen } from '@order/Telemetry';

import { useSnapPixel } from '../../../../integrations';
import { type RouteInfo, useRouteObserver } from '../useRouteObserver';
import { stopDatadogView, trackDatadogView } from './utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useTrackRoutes = () => {
  const { trackScreen } = useTrackScreen();
  const { trackPageView: trackSnapPixelPageView } = useSnapPixel();

  // ─── Refs ────────────────────────────────────────────────────────────

  const activeDatadogViewRef = useRef<RouteInfo | null>(null);
  const appStateSubscriptionRef = useRef<NativeEventSubscription | null>(null);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleRouteChange = useCallback(
    (route: RouteInfo) => {
      trackScreen(route.pathname);
      trackSnapPixelPageView({ description: route.pathname });
      trackDatadogView(route.viewKey, route.pathname);

      activeDatadogViewRef.current = route;
    },
    [trackScreen, trackSnapPixelPageView],
  );

  const handleAppStateChange = useCallback((appState: AppStateStatus) => {
    try {
      const activeView = activeDatadogViewRef.current;

      if (!activeView) return;

      if (appState === 'background') {
        stopDatadogView(activeView.viewKey);
        return;
      }

      if (appState === 'active') {
        trackDatadogView(activeView.viewKey, activeView.pathname);
      }
    } catch {}
  }, []);

  const handleAppStateCleanup = useCallback(() => {
    try {
      appStateSubscriptionRef.current?.remove();
      appStateSubscriptionRef.current = null;

      const activeView = activeDatadogViewRef.current;

      if (!activeView) return;

      stopDatadogView(activeView.viewKey);
      activeDatadogViewRef.current = null;
    } catch {}
  }, []);

  /**
   * Stops the RUM view when the app backgrounds and restarts it on foreground.
   * Required for Expo Router, where we drive view tracking manually instead of
   * registering the NavigationContainer with `DdRumReactNavigationTracking`.
   */
  const subscribeToAppStateChanges = useCallback(() => {
    appStateSubscriptionRef.current = AppState.addEventListener('change', handleAppStateChange);

    return handleAppStateCleanup;
  }, [handleAppStateChange, handleAppStateCleanup]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useRouteObserver({ onChange: handleRouteChange });
  useEffect(subscribeToAppStateChanges, [subscribeToAppStateChanges]);
};
